import * as Popover from "@radix-ui/react-popover";
import { useTranslation } from "react-i18next";
import ArrowLeft from "@/assets/icons/arrow-left.svg?react";
import BgtButton from "@/components/BgtButton/BgtButton";
import { BgtSimpleSwitch } from "@/components/BgtForm/BgtSimpleSwitch";

export type SortKey = "rank" | "name" | "played";

export interface SortRule {
	key: SortKey;
	enabled: boolean;
	descending: boolean;
}

/** The order the rules are offered in, and the order they are applied in. */
export const SORT_KEYS: SortKey[] = ["rank", "name", "played"];

export const DEFAULT_SORT: SortRule[] = [
	{ key: "rank", enabled: true, descending: false },
	{ key: "name", enabled: false, descending: false },
	{ key: "played", enabled: false, descending: true },
];

interface Props {
	rules: SortRule[];
	/** Hidden when the account is linked to nobody: there is no "mine" to count. */
	canSortByPlays: boolean;
	onChange: (rules: SortRule[]) => void;
}

/**
 * Sorting for the games list.
 *
 * Several rules can be on at once and are applied in the order they are listed
 * - rank, then name, then plays - so turning on a second one breaks ties in
 * the first rather than replacing it.
 */
export const GamesSortMenu = ({ rules, canSortByPlays, onChange }: Props) => {
	const { t } = useTranslation("games");

	const update = (key: SortKey, change: Partial<SortRule>) =>
		onChange(rules.map((rule) => (rule.key === key ? { ...rule, ...change } : rule)));

	const visible = rules.filter((rule) => rule.key !== "played" || canSortByPlays);

	return (
		<Popover.Root>
			<Popover.Trigger asChild>
				<BgtButton variant="cancel" size="2" type="button" aria-label={t("sort.label")}>
					<ArrowLeft className="size-4 -rotate-90" />
					{t("sort.label")}
				</BgtButton>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					align="start"
					sideOffset={4}
					className="z-[9999] w-72 rounded-md border border-primary/30 bg-input p-2 shadow-xl"
				>
					{visible.map((rule) => (
						<div key={rule.key} className="flex items-center gap-2 rounded px-2 py-1.5">
							<button
								type="button"
								aria-label={t(rule.descending ? "sort.descending" : "sort.ascending")}
								disabled={!rule.enabled}
								className="rounded p-1 text-primary hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent"
								onClick={() => update(rule.key, { descending: !rule.descending })}
							>
								<ArrowLeft className={`size-4 ${rule.descending ? "rotate-90" : "-rotate-90"}`} />
							</button>
							<div className="flex-1">
								<BgtSimpleSwitch
									label={t(`sort.${rule.key}`)}
									value={rule.enabled}
									onChange={(enabled) => update(rule.key, { enabled })}
								/>
							</div>
						</div>
					))}
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
};
